import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, FilterFn } from '@tanstack/react-table';

import { KollelDtoIn } from '@/models/in/KollelDtoIn';
import { KollelManagementTabType } from '@/models/common/KollelManagementTabType';
import { getKollelStatusColor, getKollelStatusLabelKey } from '@/helpers/kollelStatus';
import DataTable from '@/components/ui/DataTable/DataTable';
import cellType from '@/components/ui/DataTable/CellTypes';
import { Cell, Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import KollelStatusChangeDialog from './KollelStatusChangeDialog';

const kollelSearch: FilterFn<KollelDtoIn> = (row, _columnId, value) => {
  if (!value) return true;
  const searchValue = String(value).toLowerCase();
  const { name, fundName, createdByEmail } = row.original;
  return [name, fundName, createdByEmail].some((field) => field?.toLowerCase().includes(searchValue));
};

/**
 * The admin "אישור כוללים" queue (HAT-1446 phase 5) — the welfare-committee gate. Same shape as
 * ProjectsManagementPage: status tabs over one DataTable, the server resolving each tab to its
 * statuses.
 */
const KollelsManagementPage = () => {
  const { t } = useTranslation();

  const [selectedTab, setSelectedTab] = useState<KollelManagementTabType>(KollelManagementTabType.PendingApproval);
  const [searchQuery, setSearchQuery] = useState('');
  const [kollelToUpdate, setKollelToUpdate] = useState<KollelDtoIn | null>(null);

  const { data: kollels, isLoading, refetch } = useGetAuthApi<KollelDtoIn[]>(
    `management/kollels?tab=${selectedTab}`
  );

  const handleCloseStatusDialog = useCallback(() => setKollelToUpdate(null), []);

  const columns: ColumnDef<KollelDtoIn>[] = [
    {
      id: 'name',
      header: Col(t('kollelsManagement.columns.name')),
      accessorKey: 'name',
      enableSorting: true,
      meta: { className: 'min-w-[200px]' },
    },
    {
      id: 'fundName',
      header: Col(t('kollelsManagement.columns.fund')),
      accessorKey: 'fundName',
      enableSorting: true,
      meta: { className: 'min-w-[160px]' },
    },
    {
      id: 'avrechs',
      header: Col(t('kollelsManagement.columns.avrechs')),
      enableSorting: false,
      cell: ({ row }) => <span>{row.original.avrechs.length}</span>,
      meta: { className: 'min-w-[90px] max-w-[90px]' },
    },
    {
      id: 'status',
      header: Col(t('kollelsManagement.columns.status')),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div
            className="w-[10px] h-[10px] rounded-full shrink-0"
            style={{ backgroundColor: getKollelStatusColor(row.original.status) }}
          />
          <span>{t(getKollelStatusLabelKey(row.original.status))}</span>
        </div>
      ),
      meta: { className: 'min-w-[150px]' },
    },
    {
      id: 'statusComment',
      header: Col(t('kollelsManagement.columns.statusComment')),
      accessorKey: 'statusComment',
      enableSorting: false,
      cell: ({ row }) => (
        <span className={row.original.statusComment ? '' : 'text-(--font-tertiary)'}>
          {row.original.statusComment || '—'}
        </span>
      ),
      meta: { className: 'min-w-[180px]' },
    },
    {
      id: 'createdByEmail',
      header: Col(t('kollelsManagement.columns.createdBy')),
      accessorKey: 'createdByEmail',
      enableSorting: true,
      meta: { className: 'min-w-[180px]' },
    },
    {
      id: 'createdAt',
      header: Col(t('kollelsManagement.columns.createdAt')),
      accessorKey: 'createdAt',
      cell: Cell(cellType.Date),
      enableSorting: true,
      meta: { className: 'min-w-[130px] max-w-[130px]' },
    },
    {
      id: 'actions',
      header: Col(''),
      enableSorting: false,
      cell: ({ row }) => (
        row.original.allowedNextStatuses.length > 0 ? (
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              setKollelToUpdate(row.original);
            }}
          >
            {t('kollelsManagement.statusDialog.submit')}
          </Button>
        ) : null
      ),
      meta: { className: 'min-w-[140px]' },
    },
  ];

  return (
    <div>
      <Tabs
        value={String(selectedTab)}
        onValueChange={(value) => setSelectedTab(Number(value) as KollelManagementTabType)}
        style={{ direction: 'rtl' }}
        className="mb-6"
      >
        <TabsList>
          <TabsTrigger value={String(KollelManagementTabType.PendingApproval)}>{t('kollelsManagement.tabs.pending')}</TabsTrigger>
          <TabsTrigger value={String(KollelManagementTabType.Approved)}>{t('kollelsManagement.tabs.approved')}</TabsTrigger>
          <TabsTrigger value={String(KollelManagementTabType.Rejected)}>{t('kollelsManagement.tabs.rejected')}</TabsTrigger>
        </TabsList>
      </Tabs>

      <DataTable
        title={t('kollelsManagement.title')}
        data={kollels ?? []}
        isLoading={isLoading}
        columns={columns}
        filters={
          <div className="flex items-center justify-end gap-3">
            <SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="kollelsManagement.search" />
          </div>
        }
        emptyStateContent={{ title: t('kollelsManagement.noData'), description: t('kollelsManagement.noDataDescription') }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customGlobalSearch={kollelSearch}
      />

      {kollelToUpdate && (
        <KollelStatusChangeDialog
          open
          onClose={handleCloseStatusDialog}
          kollel={kollelToUpdate}
          onSuccess={refetch}
        />
      )}
    </div>
  );
};

export default KollelsManagementPage;
